import p5 from "p5";
import { colour_palettes as palette_impt } from "./colour_palettes.js";

// K
import {
  frand_rng,
  arr_pick, // I
  render_dims,
  calculateAspectRatioFit,
  map_shift,
} from "./local_mods/util.js"; // R

import {
  RFC,
  FC,
  SR,
  PHI,
  GA, // H
  SGR,
  EUL,
  SQR_2,
} from "./local_mods/math_constants.js";

import {
  radial_out_mode,
  button_mode,
  ll_cross_mode,
  morse_code_mode,
  pieish_chart_mode,
} from "./render_modes.js";

import { project_description_for_console } from "./local_mods/project_description_output.js";

const author = "sspboyd";
const sketch_name = "Boom_fx_max";

///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////

let myp5 = new p5((p55) => {
  console.clear();
  const launched_dt = Date.now();

  let screen_pg;
  let c; // S
  const quake_md = {
    date: "All Dates",
    desc: "All Dates",
    filename: "./data/all.geojson",
  };

  let q_data_geojson;

  const config = {
    show_palette: 1,
    show_bbox: 0,
    show_timeline: 0,
    blend: p55.HARD_LIGHT,
  };

  const render_modes = [];
  render_modes.push(ll_cross_mode);
  render_modes.push(radial_out_mode);
  render_modes.push(button_mode);
  render_modes.push(morse_code_mode);
  render_modes.push(pieish_chart_mode);

  let quakes = []; // an array of quake objects

  const set_scene = function () {
    const OPRs = [SGR, PHI, GA, SR, EUL, RFC, FC];
    const scene_OPR = arr_pick(OPRs);
    const OPR = scene_OPR.nat;
    const i_OPR = scene_OPR.inv;

    // possible centre locations (from -1 to 1)
    const map_centre_locs = [-i_OPR, -1 * (1 - i_OPR), 1 - i_OPR, i_OPR]; // options for the lat/long offsets
    // set centre location of map
    const map_centre_norms = {
      x: p55.map(arr_pick(map_centre_locs), -1, 1, 0, 1),
      y: p55.map(arr_pick(map_centre_locs), -1, 1, 0, 1),
    };

    const map_centre_offset_norms = {
      x: map_centre_norms.x - 0.5,
      y: map_centre_norms.y - 0.5,
    };

    // Colour palette
    const palette_md = arr_pick(palette_impt); // _md means metadata
    const palette_title = palette_md.title;
    const pal_idx = palette_md.pal_num;
    const palette_length = palette_md.num_clrs;
    const palette = palette_md.palette;

    const date_desc = quake_md.desc;

    const aspect = {};
    aspect.probs = {
      sqr: PHI.inv,
      port: (1 - PHI.inv) * PHI.inv,
      land: 1 - PHI.inv - (1 - PHI.inv) * PHI.inv,
    };
    aspect.name = "Square";
    aspect.layout = "square";
    aspect.ratio = { w: 1, h: 1 };

    const aspect_ratio_fact = frand_rng(1);
    if (aspect_ratio_fact > 1 - aspect.probs.land) {
      aspect.name = "Landscape";
      aspect.layout = "land";
      aspect.ratio.w = SQR_2;
      aspect.ratio.h = 1;
    } else if (aspect_ratio_fact < aspect.probs.port) {
      aspect.name = "Portrait";
      aspect.layout = "port";
      aspect.ratio.w = 1;
      aspect.ratio.h = SQR_2;
    }
    aspect.dims = render_dims[aspect.layout];

    return {
      OPR,
      i_OPR,
      scene_OPR,
      aspect,
      date_desc,
      map_centre_offset_norms,
      palette,
      palette_length,
      pal_idx,
      palette_title,
    };
  };
  const scene = set_scene(); // this will hold info that is specific to each iteration. eg clr palettes, date, duration...
  const OPR = scene.OPR;
  const i_OPR = scene.i_OPR;

  window.$fxhashFeatures = {
    "The Organizing Principle Ratio": scene.scene_OPR.title,
    "Aspect Ratio": scene.aspect.name,
    "Colour Palette": scene.palette_title,
  };

  p55.preload = () => {
    q_data_geojson = p55.loadJSON(quake_md.filename);
  };

  p55.setup = () => {
    p55.pixelDensity(1);

    // Create quake objs with data from geojson feed.
    let curr_quakes = parse_q_data(q_data_geojson);
    quakes = init_quakes(curr_quakes);
    scene.num_quakes = quakes.length;
    scene.centre_loc = { x: frand_rng(-180, 180), y: frand_rng(-90, 90) };

    let screen_dims = calculateAspectRatioFit(
      scene.aspect.ratio.w,
      scene.aspect.ratio.h,
      window.innerWidth,
      window.innerHeight
    );

    screen_dims.x = 2 * Math.round(screen_dims.x / 2);
    screen_dims.y = 2 * Math.round(screen_dims.y / 2);

    render_dims.screen = screen_dims;
    scene.aspect.dims.screen = screen_dims;

    c = p55.createCanvas(
      scene.aspect.dims.screen.x,
      scene.aspect.dims.screen.y
    );

    screen_pg = p55.createGraphics(
      scene.aspect.dims.screen.x,
      scene.aspect.dims.screen.y
    );
    screen_pg.blendMode(config.blend);
    screen_pg.strokeCap(p55.PROJECT);

    console.time("Time for One Render");
    p55.image(render(scene.aspect.dims.screen, screen_pg), 0, 0);
    console.timeEnd("Time for One Render");
    fxpreview();
    p55.noLoop();
    console.log(`Scene values:`);
    console.log({ scene });

    project_description_for_console();
    // export to file
    // export_img(p55, scene.aspect.dims.insta);
  };

  p55.draw = () => {};

  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  const render = function (curr_dims, pg) {
    pg.clear();
    pg.background(scene.palette[scene.palette.length - 1]);
    if (config.show_palette) {
      const max_dim = Math.max(curr_dims.x, curr_dims.y);
      const swatch = {
        w: max_dim,
        h: max_dim,
      };
      const old_centre = map_shift(scene.map_centre_offset_norms, {
        x: 0.5,
        y: 0.5,
        z: 0,
      });
      pg.noStroke();
      pg.fill(scene.palette[0]);
      // NW quadrant
      pg.rect(
        old_centre.x * curr_dims.x,
        old_centre.y * curr_dims.y,
        -swatch.w,
        -swatch.h
      );
      pg.fill(scene.palette[1]);
      // NE quadrant
      pg.rect(
        old_centre.x * curr_dims.x,
        old_centre.y * curr_dims.y,
        swatch.w,
        -swatch.h
      );
    }

    // Render quakes
    for (let i = 0; i < quakes.length; i++) {
      const q = quakes[i];
      q.update(curr_dims);
      q.render(q, curr_dims, pg);
    }

    return pg;
  };

  // returns an array of objs filtered to only include events within the duration window
  const parse_q_data = function (data_src) {
    let curr_quakes = [];

    for (let i = 0; i < data_src.features.length; i++) {
      let curr_q = data_src.features[i];
      let lld_loc = p55.createVector(
        curr_q.geometry.coordinates[0],
        curr_q.geometry.coordinates[1],
        curr_q.geometry.coordinates[2]
      );
      let mag = curr_q.properties.mag;
      let dt = new Date(curr_q.properties.time);
      let dt_ms = curr_q.properties.time;

      // normalized location values
      let norm_lat = p55.map(lld_loc.x, -180, 180, 0, 1);
      let norm_lon = p55.map(lld_loc.y, -90, 90, 0, 1);
      let norm_depth = p55.map(lld_loc.z, 0, 1000, 0, 1);
      let norm_loc = p55.createVector(norm_lat, norm_lon, norm_depth);

      let render_loc = p55.createVector();
      let q_name = curr_q.properties.title;
      let q_id = curr_q.id;
      let loc = { lld: lld_loc, norm: norm_loc, render: render_loc };

      let q = { dt, dt_ms, loc, mag, q_name, q_id };
      curr_quakes.push(q);
    }

    console.group("Quake Metadata:");
    console.log(`Number of quakes in this time: ${curr_quakes.length}`);

    // find max mag in the filtered set
    scene.max_mag = Math.max(...curr_quakes.map((q) => q.mag));
    console.log(`Maximum magnitude represented: ${scene.max_mag}M`);
    // find max depth in the filtered set
    scene.max_depth = Math.max(...curr_quakes.map((q) => q.loc.lld.z));
    console.log(
      `Maximum depth represented: ${Math.round(scene.max_depth)} kms`
    );
    // find min depth in the filtered set
    scene.min_depth = Math.min(...curr_quakes.map((q) => q.loc.lld.z));
    console.groupEnd();

    // sort the json array by mag and then date
    const mag_date_quakes = curr_quakes.sort(
      (q_a, q_b) => q_b.mag - q_a.mag || q_a.dt_ms - q_b.dt_ms
    );

    mag_date_quakes.forEach((q) => {
      q.render = gen_rdr_mode(render_modes, q); // assigns a render mode obj to the quake
    });
    return mag_date_quakes;
  };

  const gen_rdr_mode = function (r_modes, q) {
    const curr_rdr_m = arr_pick(r_modes);
    const rdr_m = curr_rdr_m.init_render(p55, scene, q);
    return rdr_m;
  };

  // adds update method
  const init_quakes = function (curr_quakes) {
    curr_quakes.forEach((q) => {
      q.update = function (curr_dims) {
        // if scene area has changed then update x/y locations
        const map_shifted_loc = map_shift(
          scene.map_centre_offset_norms,
          q.loc.norm
        );
        q.loc.render.x = map_shifted_loc.x * curr_dims.x;
        q.loc.render.y = map_shifted_loc.y * curr_dims.y;
      };
    });
    return curr_quakes;
  };

  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  p55.keyTyped = () => {
    keyboard_input(p55, p55.key, render_dims);
  };

  /**
   * This function handles keyboard input
   * @param {*} p55
   * @param {*} key
   * @param {*} render_dims
   */
  const keyboard_input = function (p55, key, render_dims) {
    const ca_dims = scene.aspect.dims; // ca_dims = current aspect dimensions
    console.log(ca_dims.insta.x);
    switch (key) {
      case "T":
      case "t":
        console.group(
          `Saving thumbnail at ${ca_dims.thumb.x} x ${ca_dims.thumb.y}`
        );
        export_img(p55, ca_dims.thumb);
        console.groupEnd();
        break;
      case "I":
      case "i":
        console.group(
          `Saving thumbnail at ${ca_dims.insta.x} x ${ca_dims.insta.y} for Instagram (1:1).`
        );
        export_img(p55, ca_dims.insta);
        console.groupEnd();
        break;
      case "S":
      case "s":
        console.group(
          `Saving screen size at ${ca_dims.screen.x} x ${ca_dims.screen.y}`
        );
        export_img(p55, ca_dims.screen);
        console.groupEnd();
        break;
      case "P":
      case "p":
        console.group(
          `Saving print (16" @ 300dpi) at ${ca_dims.print.x} x ${ca_dims.print.y}`
        );
        export_img(p55, ca_dims.print);
        console.groupEnd();
        break;
      case "M":
      case "m":
        console.group(
          `Saving at ${ca_dims.max.x} x ${ca_dims.max.y}. Really?? This is ridiculously large. What are you thinking??!?!?`
        );
        export_img(p55, ca_dims.max);
        console.groupEnd();
        break;
      case "H":
      case "h":
        console.group(
          `Saving at 4K on the vertial at ${ca_dims.hd4k.x} x ${ca_dims.hd4k.y}`
        );
        export_img(p55, ca_dims.hd4k);
        console.groupEnd();
        break;
      default:
        console.log(
          "Hmm. Your keyboard input is not recognized. Try using S, P, M or H to export to PNG."
        );
    }
  };

  /**
   * This function renders and image and saves it out
   * @param {*} p55
   * @param {*} dims
   */
  const export_img = function (p55, dims) {
    const export_pg = p55.createGraphics(dims.x, dims.y);
    export_pg.blendMode(config.blend);
    export_pg.strokeCap(p55.PROJECT);

    const sk_name = sketch_name;
    const res = `${dims.x}x${dims.y}`;
    // generate date string: YYYY-MM-DD-HH-MM-SS
    const dt = new Date(); // convert this to UTC?
    const datetime = `${dt.getFullYear().toString()}${(dt.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${dt.getDate().toString().padStart(2, "0")}${dt
      .getHours()
      .toString()
      .padStart(2, "0")}${dt.getMinutes().toString().padStart(2, "0")}${dt
      .getSeconds()
      .toString()
      .padStart(2, "0")}${dt.getMilliseconds().toString().padStart(3, "0")}`;
    const filename = `${sk_name}-by_${author}-${datetime}-${res}-${fxhash}`;
    console.log(`saving image to: ${filename}`);
    const output_img = render(dims, export_pg);
    output_img.save(filename);
  };
});
